import { z } from "zod";

/**
 * Sözleşme TARİHLERİ (CLOS-113 `[075]`).
 *
 * ### ⚠ Tür kataloğu KONFİGÜRASYONDAN
 *
 * Kart: *"yeni tarih türleri katalog ve versiyonlu sözleşme üzerinden eklenir, HARD-CODE
 * EDİLMEZ."* Kodda `if (type === "renewal")` yok ve olmayacak; ilişki kuralları da
 * katalogda, çünkü yeni bir tür eklemek onun ilişkisini de tanımlamayı gerektirir ve
 * ikisi ayrı yerlerde dursaydı biri gün gelip eksik kalırdı.
 *
 * ### ⚠ Gün ile AN aynı şey değil
 *
 * Kart: *"tarih veya tarih-zaman değeri, saat dilimi/yerel yorumlama kuralı ... kanonik
 * sözleşmeye göre yorumlanır; arayüz yerel gösterse de SAKLANAN ANLAM DEĞİŞTİRİLMEZ."*
 *
 * Bu yüzden iki ayrı değer alanı var ve tam biri dolu: gün hassasiyetli bir tarihi bir
 * `timestamptz`'ye çevirmek, olmayan bir saat bilgisi uydurmak olurdu — ve o uydurma
 * saat, kullanıcının saat dilimi değiştiğinde tarihi bir gün kaydırırdı.
 */

export const CONTRACT_DATE_PRECISIONS = ["day", "datetime"] as const;
export type ContractDatePrecision = (typeof CONTRACT_DATE_PRECISIONS)[number];

export const CONTRACT_DATE_SOURCES = ["manual", "document", "derived"] as const;
export type ContractDateSource = (typeof CONTRACT_DATE_SOURCES)[number];

export const contractDateConfigSchema = z.object({
  types: z
    .array(
      z.object({
        code: z.string().min(1),
        label: z.string().min(1),
        /** Kritik tarih değişikliği audit'e DEĞERİYLE giriyor (kart kriteri). */
        critical: z.boolean(),
        /** Kullanıcı tanımlı kritik tarihler birden çok olabilir; standart türler tek. */
        multiple: z.boolean(),
      }),
    )
    .min(1),
  /**
   * İLİŞKİ KURALLARI — yalnız İKİSİ DE VAR olduğunda uygulanıyor.
   *
   * Kart: *"yalnız mevcut tarihler arasındaki uygulanabilir ilişkiler doğrulanır ...
   * bitiş veya yenileme tarihi olmayan süresiz sözleşme sırf bu alanlar boş diye geçersiz
   * sayılmaz."*
   */
  relations: z.array(
    z.object({
      before: z.string().min(1),
      after: z.string().min(1),
      message: z.string().min(1),
    }),
  ),
  /** "Yaklaşan" penceresi gün cinsinden — sınıflandırma eşiği koda gömülmüyor. */
  upcomingWindowDays: z.number().int().positive(),
  labelMaxLength: z.number().int().positive(),

  /**
   * HATIRLATMA KURALLARI (CLOS-232 `[182]`).
   *
   * ### ⚠ SEÇİCİ `critical` BAYRAĞI DEĞİL, AYRI BİR LİSTE
   *
   * "Kritik olanlara hatırlat" doğal görünüyor ve YANLIŞ olurdu: katalogda `custom`
   * (kullanıcı tanımlı kritik tarih) `critical: false` taşıyor — bayrak "değişikliği
   * audit'e değeriyle girer mi" sorusunu cevaplıyor, "hatırlatılır mı" sorusunu değil.
   * Bayrağa bağlasaydık kartın AÇIKÇA saydığı bir türü sessizce düşürürdük. Ölçüldü.
   *
   * ### ⚠ PLAN KONFİGDE, DEFTER VERİTABANINDA (`contractReview` emsali)
   *
   * Hangi eşiğin ne zaman geleceği burada; tabloda yalnız "bu tarih için bu eşik geçildi"
   * kaydı duruyor. Planı satır satır önceden yazsaydık konfig değiştiğinde eski eşiklerle
   * çalışan planlar kalır ve iki gerçek kaynağı olurdu.
   */
  reminders: z.object({
    /** Hatırlatma üretilen tarih TÜRLERİ — kartın saydığı küme. */
    dateTypes: z.array(z.string().min(1)).min(1),
    /**
     * ⚠ GÜN HASSASİYETLİ TARİHİN ANI YOKTUR — bu yüzden burada bir karar duruyor.
     *
     * `precision: "day"` satırında saat dilimi kolonu şema gereği NULL: takvim günü
     * dilimden bağımsız bir kavram. Ama "3 gün önce hatırlat" bir AN istiyor. Diliml ve
     * yerel saat buradan geliyor; koda gömmek, kararı görünmez kılardı.
     *
     * ⚠ ACIK-KARAR (CLOS-232): alan (tenant) diliminin kullanılması ürün tarafından
     * tercih edilirse ŞEMA İŞİ — ölçüldü, `tenants` tablosunda saat dilimi kolonu YOK
     * (`users.timezone` var ama hatırlatma kullanıcıya değil tarihe ait).
     */
    dayPrecisionTimezone: z.string().min(1),
    dayPrecisionLocalHour: z.number().int().min(0).max(23),
    /**
     * Eşikler: tarihe kalan güne göre. `daysBefore` NEGATİF ise geçmişi anlatır
     * (-1 = tarihten bir gün sonra). `key` deftere yazılır ve gün değeri değişse bile
     * "bu eşik için hatırlattık" gerçeğini korur — kimlik, içerik değil (CLOS-194 dersi).
     */
    thresholds: z
      .array(
        z.object({
          key: z.string().min(1),
          daysBefore: z.number().int(),
        }),
      )
      .min(1),
    /**
     * GEÇMİŞ EŞİK TABANI — kaç gün geriye kadar hatırlatılır.
     *
     * ⚠ Bu bir GERİYE DÖNÜK PATLAMA kalkanı. Defter boşken ilk süpürme, eşiği çoktan
     * geçmiş BÜTÜN tarihleri "yeni" sayar: beş yıl önce dolmuş bir yenileme tarihi de,
     * dün dolan da aynı anda bildirim üretir. Kartın *"geçmişte kalmış ve artık anlamlı
     * olmayan çalışma zamanı için gereksiz geriye dönük bildirim üretilmez"* kuralı tam
     * olarak bunu yasaklıyor.
     *
     * ⚠ Sayı KONFİGDE çünkü "ne kadar geçmiş hâlâ anlamlı" bir ürün kararı; koda
     * gömseydik o kararı görünmez kılardık.
     *
     * ⚠ BU ALANI BÜYÜTMEK GEÇMİŞE DÖNÜK BİLDİRİM ÜRETİR — yan etki değil, tasarım.
     * Tabanın dışında kalan eşik deftere YAZILMIYOR (bkz. `contractDates/reminders.ts`),
     * yani "atlandı" diye işaretlenmiyor. Bunun simetrik ucu şu: taban yarın 14'ten 90'a
     * çıkarılırsa, o aralıkta kalmış bütün eşikler ilk süpürmede TOPLU ateşlenir.
     * Bilinçli — bugün atlanan bir eşik, kural genişlerse ateşlenebilmeli — ama sessiz
     * değil: bu alanı büyüten kişi ne olacağını burada okur.
     */
    backlogDays: z.number().int().positive(),
  }),
});
export type ContractDateConfig = z.infer<typeof contractDateConfigSchema>;
export type ContractDateTypeOption = ContractDateConfig["types"][number];

const ISO_GUN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Tarih girdisi.
 *
 * `precision: "day"` → `value` bir takvim günü (`YYYY-MM-DD`) ve saat dilimi YOK: takvim
 * günü zaten saat diliminden bağımsız bir kavram. Zorunlu kılsaydık, "31 Aralık'ta biter"
 * cümlesine ait olmayan bir dilim iliştirmiş olurduk.
 *
 * `precision: "datetime"` → `value` bir ISO anı ve `timezone` ZORUNLU: an, kullanıcının
 * onu hangi yerel duvar saatinde yazdığını bilmeden geri gösterilemez.
 */
export const contractDateInputSchema = z
  .object({
    dateType: z.string().min(1, "Tarih türü zorunludur"),
    /** Yalnız kullanıcı tanımlı kritik tarihlerde; standart türlerin adı katalogdan. */
    label: z.string().trim().max(120).nullish(),
    precision: z.enum(CONTRACT_DATE_PRECISIONS, "Geçerli bir hassasiyet seçilmelidir"),
    value: z.string().min(1, "Tarih zorunludur"),
    /** IANA saat dilimi — `datetime` hassasiyetinde zorunlu. */
    timezone: z.string().min(1).nullish(),
    source: z.enum(CONTRACT_DATE_SOURCES).default("manual"),
    note: z.string().max(500, "Not en fazla 500 karakter olabilir").nullish(),
  })
  .superRefine((deger, ctx) => {
    if (deger.precision === "day") {
      if (!ISO_GUN.test(deger.value)) {
        ctx.addIssue({
          code: "custom",
          path: ["value"],
          message: "Tarih YYYY-AA-GG biçiminde olmalıdır",
        });
      }
      if (deger.timezone) {
        ctx.addIssue({
          code: "custom",
          path: ["timezone"],
          message: "Gün hassasiyetli tarihe saat dilimi verilemez",
        });
      }
      return;
    }
    if (Number.isNaN(Date.parse(deger.value))) {
      ctx.addIssue({ code: "custom", path: ["value"], message: "Geçerli bir tarih-saat giriniz" });
    }
    if (!deger.timezone) {
      ctx.addIssue({
        code: "custom",
        path: ["timezone"],
        message: "Tarih-saat için saat dilimi zorunludur",
      });
    }
  });
export type ContractDateInput = z.infer<typeof contractDateInputSchema>;

/**
 * SINIFLANDIRMA — kart: *"yaklaşan veya geçen tarihler doğru sınıflanır."*
 *
 * ⚠ Sonuç SAKLANMIYOR, okuma anında hesaplanıyor: saklansaydı saat ilerlediği anda
 * bayatlar ve "yaklaşan" bir tarih, kimse dokunmadığı için sonsuza kadar yaklaşan kalırdı.
 */
export const CONTRACT_DATE_STANDINGS = ["past", "today", "upcoming", "future"] as const;
export type ContractDateStanding = (typeof CONTRACT_DATE_STANDINGS)[number];

export const CONTRACT_DATE_STANDING_NAMES: Record<ContractDateStanding, string> = {
  past: "Geçti",
  today: "Bugün",
  upcoming: "Yaklaşıyor",
  future: "İleride",
};

const GUN_MS = 86_400_000;

/**
 * Bir tarihi bugüne göre sınıflandırır.
 *
 * Karşılaştırma GÜN düzeyinde: bir anın "geçmiş" olması için günün bitmesi gerekiyor.
 * Ham `Date` karşılaştırmasıyla yapsaydık, bugün saat 09:00'daki bir toplantı saat
 * 10:00'da "geçti" olurdu — oysa kullanıcı için o tarih hâlâ bugündür.
 */
export function classifyContractDate(
  isoGun: string,
  bugunIsoGun: string,
  upcomingWindowDays: number,
): ContractDateStanding {
  const hedef = Date.parse(`${isoGun}T00:00:00Z`);
  const bugun = Date.parse(`${bugunIsoGun}T00:00:00Z`);
  if (hedef < bugun) return "past";
  if (hedef === bugun) return "today";
  return hedef - bugun <= upcomingWindowDays * GUN_MS ? "upcoming" : "future";
}

/**
 * SLOT ANAHTARI — bir sözleşmede bir tarihin "yeri".
 *
 * Standart türlerde slot türün kendisi: bir sözleşmenin iki bitiş tarihi olamaz. Kullanıcı
 * tanımlı kritik tarihlerde slot etikete bağlı, çünkü kart birden çoğuna izin veriyor —
 * ama aynı adla ikincisi yine yinelenmedir ve karar veritabanına bırakılıyor (`[073]`
 * kimlik anahtarıyla aynı desen).
 */
export function contractDateSlotKey(dateType: string, label?: string | null): string {
  if (!label) return dateType;
  return `${dateType}:${label.trim().replace(/\s+/g, " ").toLocaleLowerCase("tr")}`;
}

export type ContractDate = {
  id: string;
  dateType: string;
  label: string | null;
  precision: ContractDatePrecision;
  /** Gün hassasiyetinde `YYYY-MM-DD`, tarih-saatte ISO an. */
  value: string;
  timezone: string | null;
  source: ContractDateSource;
  note: string | null;
  /** Sınıflandırma okuma anında hesaplanıyor; satırda saklanmıyor. */
  standing: ContractDateStanding;
  updatedAt: string;
};

export type ContractDateList = {
  dates: ContractDate[];
  /** Katalog SUNUCUDAN: arayüz kendi tür listesini tutmuyor (`[072]` ile aynı karar). */
  types: ContractDateTypeOption[];
};
